import React from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import BaseScreen from "../components/base/BaseScreen";
import StorageEngine from "../utils/StorageEngine";
import appConfig from "../config/AppConfig";
import DomainUtils from "../utils/DomainUtils";
import { ActionTypes } from "../redux/actionTypes/screens/SchoolSelectionActionTypes";
import { Actions, reducer, INITIAL_STATE } from "../redux/screens/SchoolSelectionRedux";
import UIDs from "../UIDs";

// MUI imports
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Pagination from "@mui/material/Pagination";
import Divider from "@mui/material/Divider";
import { MagnifyingGlass as SearchIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { GraduationCap as SchoolIcon } from "@phosphor-icons/react/dist/ssr/GraduationCap";
const uid = UIDs.screens.schoolSelection;

// Styles are now defined inline using sx prop
// Constants for pagination
const ITEMS_PER_PAGE = 10;

class SchoolSelectionPage extends BaseScreen {
    get routeName() {
        return uid;
    }

    get reducer() {
        return reducer;
    }

    get actionHandlers() {
        return {
            [ActionTypes.LOAD_SCHOOLS_ASYNC]: { done: this._handleLoadSchoolsAsyncDone, error: this._handleLoadSchoolsAsyncError },
        };
    }

    constructor(props) {
        super(props);
        this.state = {
            currentPage: 1,
            viewMode: "grid", // 'grid' or 'list'
        };
    }

    componentDidMount() {
        super.componentDidMount();
        // Load schools when component mounts
        this.props.loadSchoolsAsync();
    }

    _handleLoadSchoolsAsyncDone = () => {
        // Any additional handling after schools are loaded
        console.log("Schools loaded successfully");
    };

    _handleLoadSchoolsAsyncError = (action) => {
        // Handle error
        this.displayError(action.error);
    };

    handleSearchChange = (e) => {
        this.props.searchSchools(e.target.value);
        this.setState({ currentPage: 1 }); // Reset to first page on search
    };

    handleSchoolSelect = (school) => {
        // Save selected school networkId to localStorage (including new customDomain)
        StorageEngine(appConfig.LAST_VISITED_NETWORK_ID_KEY).save({
            networkId: school.networkId,
            idValue: school.idValue,
            customDomain: school.customDomain
        });

        // Update Redux state
        this.props.selectSchool(school);

        // Check if we should redirect back to offerings page
        const searchParams = new URLSearchParams(this.props.location.search);
        const goBackToOffering = searchParams.get('goBackToOffering');

        // Use customDomain for navigation if available and in subdomain mode, otherwise use idValue
        const navigationId = (DomainUtils.isSubdomainMode()) ? "" : school.idValue;

        if (goBackToOffering === 'true') {
            // Navigate to the school's offerings page
            this.props.history.push(navigationId !== "" ? `/${navigationId}/portal/offerings` : `/portal/offerings`);
        } else {
            // Navigate to the school's default page
            this.props.history.push(navigationId !== "" ? `/${navigationId}/` : "/");
        }
    };

    handlePageChange = (event, page) => {
        this.setState({ currentPage: page });
    };

    toggleViewMode = () => {
        this.setState(prevState => ({
            viewMode: prevState.viewMode === "grid" ? "list" : "grid"
        }));
    };

    getPaginatedSchools(schools) {
        const { currentPage } = this.state;
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return schools.slice(startIndex, endIndex);
    }

    renderSchoolCard(school) {
        return (
            <Card
                elevation={2}
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s",
                    "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: 4,
                    },
                }}>
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Avatar
                            src={school.logo}
                            alt={school.name}
                            sx={{
                                width: 80,
                                height: 80,
                                mb: 2,
                                bgcolor: school.mainNavigColor || "primary.main",
                                fontSize: "2rem",
                            }}
                        >
                            {!school.logo || school.logo.includes("NoImage") ? school.name.charAt(0).toUpperCase() : null}
                        </Avatar>
                        <Typography variant="h6" component="h3" align="center">
                            {school.name}
                        </Typography>
                    </Box>
                </CardContent>
                <CardActions>
                    <Button fullWidth variant="contained" color="primary" onClick={() => this.handleSchoolSelect(school)}>
                        Select
                    </Button>
                </CardActions>
            </Card>
        );
    }

    renderGridView(schools) {
        return (
            <Grid container spacing={3}>
                {schools.map((school) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={school.id}>
                        {this.renderSchoolCard(school)}
                    </Grid>
                ))}
            </Grid>
        );
    }

    renderListView(schools) {
        return (
            <List sx={{ bgcolor: "background.paper", borderRadius: 1, boxShadow: 1 }}>
                {schools.map((school, index) => (
                    <React.Fragment key={school.id}>
                        <ListItem
                            alignItems="center"
                            sx={{
                                py: 2,
                                transition: "background-color 0.2s",
                                "&:hover": { bgcolor: "action.hover" },
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar
                                    src={school.logo}
                                    alt={school.name}
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        bgcolor: school.mainNavigColor || "primary.main",
                                    }}
                                >
                                    {!school.logo || school.logo.includes("NoImage") ? school.name.charAt(0).toUpperCase() : null}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" sx={{ ml: 2 }}>
                                        {school.name}
                                    </Typography>
                                }
                                sx={{ ml: 2 }}
                            />
                            <Button variant="contained" color="primary" onClick={() => this.handleSchoolSelect(school)}>
                                Select
                            </Button>
                        </ListItem>
                        {index < schools.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                ))}
            </List>
        );
    }

    renderSchoolsList() {
        const { localState } = this.props;
        const { filteredSchools, searchTerm } = localState;
        const { viewMode } = this.state;

        if (filteredSchools.length === 0) {
            return (
                <Box
                    sx={{
                        textAlign: "center",
                        padding: 4,
                    }}>
                    <Typography variant="h6" gutterBottom>
                        No schools found matching &quot;{searchTerm}&quot;
                    </Typography>
                    <Button variant="outlined" color="primary" onClick={() => this.props.searchSchools("")}>
                        Clear Search
                    </Button>
                </Box>
            );
        }

        // Get paginated schools
        const paginatedSchools = this.getPaginatedSchools(filteredSchools);
        const totalPages = Math.ceil(filteredSchools.length / ITEMS_PER_PAGE);

        return (
            <>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.toggleViewMode}
                        startIcon={viewMode === "grid" ? <SchoolIcon /> : <SchoolIcon />}
                    >
                        {viewMode === "grid" ? "List View" : "Grid View"}
                    </Button>
                </Box>

                {viewMode === "grid" ? this.renderGridView(paginatedSchools) : this.renderListView(paginatedSchools)}

                {totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                        <Pagination
                            count={totalPages}
                            page={this.state.currentPage}
                            onChange={this.handlePageChange}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}
            </>
        );
    }

    render() {
        const { localState } = this.props;
        const { filteredSchools } = localState || { filteredSchools: [] };

        if (this.isActionExecuting(ActionTypes.LOAD_SCHOOLS_ASYNC)) {
            return (
                <Container
                    sx={{
                        padding: 4,
                        maxWidth: 1200,
                        margin: "0 auto",
                    }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "50vh",
                            flexDirection: "column",
                        }}>
                        <CircularProgress size={60} />
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6">Loading schools...</Typography>
                        </Box>
                    </Box>
                </Container>
            );
        }

        return (
            <Container
                sx={{
                    padding: 4,
                    maxWidth: 1200,
                    margin: "0 auto",
                }}>
                <Box
                    sx={{
                        textAlign: "center",
                        marginBottom: 4,
                    }}>
                    <Typography variant="h3" component="h1" sx={{ marginBottom: 1 }}>
                        Welcome to IMLeagues
                    </Typography>
                    <Typography
                        variant="h5"
                        component="p"
                        sx={{
                            marginBottom: 3,
                            color: "text.secondary",
                        }}>
                        Select your school to continue
                    </Typography>

                    <Box
                        sx={{
                            maxWidth: 600,
                            margin: "0 auto",
                            marginBottom: 2,
                        }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search for your school..."
                            value={localState?.searchTerm || ""}
                            onChange={this.handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {filteredSchools && filteredSchools.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Showing {Math.min(ITEMS_PER_PAGE, filteredSchools.length)} of {filteredSchools.length} schools
                        </Typography>
                    )}
                </Box>

                {this.renderSchoolsList()}
            </Container>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        localState: state.screens[reducer.key] ? state.screens[reducer.key] : INITIAL_STATE,
        userId: state.session.userId,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        loadSchoolsAsync: () => dispatch(Actions.loadSchoolsAsync()),
        searchSchools: (vo) => dispatch(Actions.searchSchools(vo)),
        selectSchool: (vo) => dispatch(Actions.selectSchool(vo)),
    };
};

// No need for a style wrapper with MUI v6
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SchoolSelectionPage));
